/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core import
import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as PapaParse from 'papaparse';

// Component imports
import { Button, Col, Divider, Modal, Row, Tooltip, Typography, Upload } from 'antd';

// Misc imports
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../constants/flowGraph';
import { automatedAnswerToChaptersource } from '../../utils/transform/automatedAnswer';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useDeleteAutomatedAnswerMutation, useDeleteAutomatedAnswersMutation, useUpdateAutomatedAnswersMutation } from '../../store/automatedAnswers/automatedAnswersApi';
import { ACTIVITY_TYPES } from '../../constants/main';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';
import exportAutomatedAnswers, { AA_INNER_DELIMITER } from '../../utils/misc/exportAutomatedAnswers';

// Constant declarations
const CSV_PARSER_OPTIONS = {
  header: true,
  dynamicTyping: true,
  skipEmptyLines: true,
  transformHeader: header => header.toLowerCase().replace(/\W/g, '_'),
  transform: (value, header) => {
    // Break down examples based on their delimiters
    if (header.includes('examples')) {
      return [...(value.split(AA_INNER_DELIMITER) || []).map(example => example.trim())];
    }

    // Else, return the value as is
    return value;
  }
};

const convertCsvDataToAutomatedAnswers = (csvData, primaryBotLanguage, t) => {
  if (!csvData?.data) {
    openConsoleAlertMessage({ type: 'error', message: t('Invalid CSV file') });
    return null;
  }

  // Check if the CSV has the required columns
  const requiredColumns = [`${primaryBotLanguage}_name`, `${primaryBotLanguage}_examples`, `${primaryBotLanguage}_responses`];
  const missingColumns = requiredColumns.filter(column => !csvData.meta.fields.includes(column));

  if (missingColumns.length > 0) {
    openConsoleAlertMessage({ type: 'error', message: t('Missing columns: {{columnsCount}}', { columnsCount: missingColumns.join(', ') }) });
    return null;
  }

  // Show error if any response field for the primary language is empty and there is no redirect either (which means there is no bot response at all)
  const entriesWithNoPrimaryResponseCount = (csvData.data || []).filter(entry => !entry[`${primaryBotLanguage}_responses`] && !entry.redirect).length;
  if (entriesWithNoPrimaryResponseCount > 0) {
    openConsoleAlertMessage({ type: 'error', message: t('There are {{entriesCount}} entries with no responses for the primary language.', { entriesCount: entriesWithNoPrimaryResponseCount }) });
    return null;
  }

  return (csvData.data || []).map(entry => {
    // Merge el_name, en_name, etc. into name
    // Final format should be name: { el: 'Greek name', en: 'English name' }
    const name = Object.keys(entry)
      .filter(key => key.includes('name'))
      .reduce((acc, key) => {
        const language = key.split('_')[0];
        // Only add the key-value pair if the value is not null
        if (entry[key] !== null) {
          return {
            ...acc,
            [language]: entry[key]
          };
        }
        return acc;
      }, {});

    // Merge el_group, en_group, etc. into group
    // Final format should be group: { el: 'Greek group', en: 'English group' }
    const group = Object.keys(entry)
      .filter(key => key.includes('group'))
      .reduce((acc, key) => {
        const language = key.split('_')[0];
        return {
          ...acc,
          [language]: entry[key]
        };
      }, {});

    // Merge el_examples, en_examples, etc. into examples array
    // Final format should be examples: { el: ['example1', 'example2'], en: ['example1', 'example2', 'example3'] }
    const examples = Object.keys(entry)
      .filter(key => key.includes('examples'))
      .reduce((acc, key) => {
        const language = key.split('_')[0];
        return {
          ...acc,
          [language]: entry[key]
        };
      }, {});

    const tags = Object.keys(entry)
      .filter(key => key.includes('tags'))
      .reduce((acc, key) => {
        const _tags = entry[key]?.split(AA_INNER_DELIMITER) || [];
        return [...acc, ..._tags];
      }, []);

    // Merge el_response, en_response and jump into responses array
    // Current format is:
    /* entry: {
        en_name: "Aaa",
        en_group: "Default",
        el_examples: [
          "Grek example",
          "EXAMPL",
        ],
        en_examples: [
          "En example",
          "EEEEX",
        ],
        el_responses: "Greek response1///Greek response2",
        en_responses: "English response///English response2",
        fr_responses: "French response1///French response2",
        redirect: "introNodeId",
      }
    */
    // Final format should be
    /*
      responses: [
        { type: NODE_TYPES.MESSAGE, options: [{ el: 'Greek response1', en: 'English response1', fr: 'French response1' }] },
        { type: NODE_TYPES.MESSAGE, options: [{ el: 'Greek response2', en: 'English response2', fr: 'French response2' }] },
        { type: NODE_TYPES.JUMP, jumpTo: 'introNodeId' }
      ]
    */
    let responses = [];

    if (Object.keys(entry).filter(key => key.includes('responses'))) {
      // Get all languages that have responses
      const languages = (Object.keys(entry) || [])
        .filter(key => key.includes('responses'))
        .map(key => key.split('_')?.[0]);

      // Get the responses count for the primary language
      const primaryResponsesCount = entry[`${primaryBotLanguage}_responses`]?.split(AA_INNER_DELIMITER).length || 0;

      for (let i = 0; i < primaryResponsesCount; i++) {
        // For each response, get the responses per language and add them to the responses
        const optionsList = languages.map(language => {
          return {
            [language]: entry[`${language}_responses`]?.split(AA_INNER_DELIMITER)[i]
          };
        });

        responses = [
          ...responses,
          {
            type: NODE_TYPES.MESSAGE,
            // Options is an array with a single object, which is a key value pair of language and response per language
            // Like [{ el: 'Greek response1', en: 'English response1', fr: 'French response1' }]
            options: [Object.assign({}, ...optionsList)]
          }
        ];
      }
    }

    // If there is a redirect, add it to the responses
    if (entry.redirect) {
      responses.push({
        type: NODE_TYPES.JUMP,
        jumpTo: entry.redirect
      });
    }

    const automatedAnswer = {
      id: entry.id,
      name: name,
      group: group,
      tags: tags,
      nlpTrainingData: {
        examples: examples
      },
      responses: responses
    };

    return automatedAnswerToChaptersource(automatedAnswer, primaryBotLanguage);
  });
};

const AutomatedAnswersImportModal = ({ onClose = () => {}, isModalOpen }) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  const { data: automatedAnswers, isFetching: isLoadingAutomatedAnswers } = getCurrentQueryState('fetchAutomatedAnswers', { orgId, tenantId, currentBotLanguage });
  const [updateAutomatedAnswers, { isLoading: isUpdatingAutomatedAnswers }] = useUpdateAutomatedAnswersMutation();
  const [deleteAutomatedAnswers] = useDeleteAutomatedAnswersMutation();
  const [, { isLoading: isDeletingAutomatedAnswer }] = useDeleteAutomatedAnswerMutation({ fixedCacheKey: 'delete-automated-answer-main-button-shared' });

  const automatedAnswersToExport = React.useMemo(() => {
    return (automatedAnswers?.items || [])
      .filter(a => a.id !== DEFAULT_FALLBACK_ID) // Ignore default fallback
      .filter(a => a.type === ACTIVITY_TYPES.AUTOMATED_ANSWERS); // Ignore chaptersources that are not automated answers (e.g. Articles)
  }, [automatedAnswers]);

  return (
    <Modal
      title={t('Import / Export Automated Answers')}
      open={isModalOpen}
      onCancel={onClose}
      style={{ maxWidth: 600 }}
      width={'95%'}
      centered
      footer={null}
    >
      <Row gutter={[24, 18]}>
        <Col xs={24} sm={10}>
          <Tooltip title={automatedAnswersToExport.length === 0 ? 'There are no Automated Answers to export' : ''}>
            <Button
              type="primary"
              size="large"
              block
              onClick={() => exportAutomatedAnswers(automatedAnswersToExport, primaryBotLanguage)}
              loading={isLoadingAutomatedAnswers || isUpdatingAutomatedAnswers || isDeletingAutomatedAnswer}
              disabled={automatedAnswersToExport.length === 0}
            >
              {t('Export as CSV')}
            </Button>
          </Tooltip>
        </Col>

        <Col xs={24} sm={14} className="align-self-center">
          <Typography.Text type="secondary">
            {t('Create a backup of the current intents.')}
          </Typography.Text>
        </Col>

        <Col xs={24} sm={10}>
          <Upload
            accept=".csv"
            className="ant-upload-w-100"
            showUploadList={false}
            beforeUpload={file => {
              const reader = new FileReader();
              reader.onload = async (event) => {
                const csvData = PapaParse.parse(
                  event.target.result,
                  Object.assign(CSV_PARSER_OPTIONS, {
                    error: () => {},
                    encoding: 'UTF-8'
                  })
                );

                const filteredCsvData = {
                  ...csvData,
                  data: csvData?.data?.filter(row => row[`${primaryBotLanguage}_name`] && row[`${primaryBotLanguage}_group`])
                };

                // Convert data to our schema
                const importedAutomatedAnswers = convertCsvDataToAutomatedAnswers(filteredCsvData, primaryBotLanguage, t);

                if (importedAutomatedAnswers) {
                  // This action will DELETE all other automated answers. To handle it, we need to:
                  // Compare the stored list of automated answers to the new one
                  // Automated answers that are not in the new list should be deleted
                  // Send two requests:
                  // 1. Update the chaptersources with the new list
                  // 2. Delete the rest
                  const automatedAnswersToDelete = (automatedAnswers?.items || [])
                    .filter(a => !importedAutomatedAnswers.some(c => c.id === a.id))
                    .filter(a => a.id !== DEFAULT_FALLBACK_ID) // Do not delete default fallback
                    .filter(a => a.type === ACTIVITY_TYPES.AUTOMATED_ANSWERS) // Do not delete chaptersources that are not automated answers (e.g. Articles)
                    .map(a => a.id);

                  const isUpdateSuccessful = await updateAutomatedAnswers({ orgId, tenantId, data: importedAutomatedAnswers }).unwrap();

                  if (isUpdateSuccessful) {
                    if (automatedAnswersToDelete.length > 0) {
                      await deleteAutomatedAnswers({ orgId, tenantId, data: automatedAnswersToDelete, successMessage: { showMessage: false } });
                    }

                    onClose();
                  }
                }
              };

              reader.readAsText(file);
              return false;
            }}
          >
            <Button type="primary" size="large" block loading={isLoadingAutomatedAnswers || isUpdatingAutomatedAnswers || isDeletingAutomatedAnswer}>
              {t('Restore from CSV')}
            </Button>
          </Upload>
        </Col>

        <Col xs={24} sm={14} className="align-self-center">
          <Typography.Text type="secondary">
            {t('Replace the current intents with new ones. All the intents in the older version will be deleted.')}
          </Typography.Text>
        </Col>

        <Col xs={24} sm={10}>
          <Upload
            accept=".csv"
            className="ant-upload-w-100"
            showUploadList={false}
            beforeUpload={file => {
              const reader = new FileReader();
              reader.onload = async (event) => {
                const csvData = PapaParse.parse(
                  event.target.result,
                  Object.assign(CSV_PARSER_OPTIONS, {
                    error: () => {},
                    encoding: 'UTF-8'
                  })
                );

                const filteredCsvData = {
                  ...csvData,
                  data: csvData?.data?.filter(row => row[`${primaryBotLanguage}_name`] && row[`${primaryBotLanguage}_group`])
                };

                // Convert data to our schema
                const importedAutomatedAnswers = convertCsvDataToAutomatedAnswers(filteredCsvData, primaryBotLanguage);

                if (importedAutomatedAnswers) {
                  const isUpdateSuccessful = await updateAutomatedAnswers({ orgId, tenantId, data: importedAutomatedAnswers }).unwrap();
                  if (isUpdateSuccessful) {
                    onClose();
                  }
                }
              };

              reader.readAsText(file);
              return false;
            }}
          >
            <Button type="primary" size="large" block loading={isLoadingAutomatedAnswers || isUpdatingAutomatedAnswers || isDeletingAutomatedAnswer}>
              {t('Import from CSV')}
            </Button>
          </Upload>
        </Col>

        <Col xs={24} sm={14} className="align-self-center">
          <Typography.Text type="secondary">
            {t('Upload new intents without deleting the current ones. Intents with the same name will be replaced with the newer version.')}
          </Typography.Text>
        </Col>

        <Divider/>

        <Col xs={24} sm={10}>
          <Button type="primary" size="large" block>
            <a
              href={`${window.location.origin.toString()}/automated_answers_sample.xlsx`}
              download
              target="_blank"
              rel="noreferrer"
            >
              {t('Download Sample XLSX')}
            </a>
          </Button>
        </Col>

        <Col xs={24} sm={14} className="align-self-center">
          <Typography.Text type="secondary">
            {t('Download a sample file with instructions for importing automated answers.')}
          </Typography.Text>
        </Col>
      </Row>
    </Modal>
  );
};

export default AutomatedAnswersImportModal;

AutomatedAnswersImportModal.propTypes = {
  onClose: PropTypes.func,
  isModalOpen: PropTypes.bool
};
